import { makeAutoObservable, runInAction } from 'mobx'
import { logger, removeSpacesFromNumericString } from 'shared/lib'
import { RequestCancelAxios } from 'shared/api/lib'
import { type IContactAll } from 'entities/Contacts'
import { type IParamsContactsSearch, type IResponseContactAll } from 'entities/Contacts/api/types'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { type IIntegrationsTabKey } from 'widgets/ContactsSearch'

export class ContactsSearchStore {
  private _cancelRequest = new RequestCancelAxios()
  private _itemsMap: Map<IResponseContactAll['id'], IContactAll> = new Map()
  private _loading = false
  private _loadingMore = false
  private _total = 0
  private _pagination: IParamsContactsSearch = {
    per_page: 10,
    page: 1,
    skip_opted_out: 0,
  }
  private _type: IIntegrationsTabKey

  constructor(type: IIntegrationsTabKey, private _entities: IEntitiesStore) {
    makeAutoObservable(this)

    this._type = type
  }

  get loadingMore() {
    return this._loadingMore
  }

  get loading() {
    return this._loading
  }

  get total() {
    return this._total
  }

  get cancelRequest() {
    return this._cancelRequest
  }

  get hasItems() {
    return Boolean(this._itemsMap.size)
  }

  get hasMore() {
    const loadedCount = this._pagination.page * (this._pagination.per_page || 10)

    return loadedCount < this._total
  }

  get items() {
    return Array.from(this._itemsMap.values())
  }

  fetch = async ({ more = false, search = '' } = {}) => {
    try {
      runInAction(() => {
        if (more) this._loadingMore = true
        else this._loading = true
      })

      const params = this._pagination

      if (more) {
        params.page += 1
      }

      if (search) {
        this.resetItems()
        params.term = removeSpacesFromNumericString(search)
        params.page = 1
      }

      if (this._type === 'selected') return

      const response = await this._entities.contactsStore.getIntegrationSearchContact(
        this._type,
        params,
        this._cancelRequest.token
      )

      if (response?.contacts) this.addItems(response?.contacts)

      runInAction(() => {
        this._total = response?.total || 0
      })
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._loadingMore = false
        this._loading = false
      })
    }
  }

  addItems = (items: IContactAll[]) => {
    items.forEach((item) => {
      this._itemsMap.set(item.id, item)
    })
  }

  resetItems = () => {
    this._itemsMap.clear()
  }
}
